import type { BadgeTone } from '@/features/common/types/BadgeTone';
// Shared Romanian labels for workflow keys, states and roles (cabinet-wide).
export const WORKFLOW_LABELS: Record<string, string> = {
  'content-approval': 'Aprobare conținut',
  'library-approval': 'Resursă bibliotecă',
  'advisor-validation': 'Validare consilier',
  'provider-approval': 'Aprobare furnizor',
  'training-approval': 'Aprobare instruire',
  'research-approval': 'Aprobare cercetare',
  'association-approval': 'Aprobare asociație',
  'event-approval': 'Aprobare eveniment',
  'funding-application': 'Cerere de finanțare',
};

/**
 * What is being decided on, named rather than identified.
 *
 * The queue printed the subject type as the backend stores it — a row read "Aprobare cercetare ·
 * organization · flux v62". `organization` is a table name and the version is the number of the
 * definition the instance is pinned to; neither says anything to the person deciding, and the
 * version is already in the audit entry every decision writes.
 *
 * The raw value is the fallback, so a subject type added server-side shows as itself rather than
 * as nothing at all.
 */
export const SUBJECT_LABELS: Record<string, string> = {
  organization: 'Organizație',
  content_item: 'Material',
  // An edit waiting on an article that is already public — not a new material, which is why it
  // reads differently in the queue: the approver is deciding on a change, not on a publication.
  content_revision: 'Modificare material',
  research_project: 'Proiect de cercetare',
  research_result: 'Rezultat de cercetare',
  library_resource: 'Resursă de bibliotecă',
  advisor_profile: 'Profil de consilier',
  // A change to a validated or attested profile, decided on as a change: the registry keeps the
  // approved profile until it is approved (T154). The same for a published provider's service.
  advisor_profile_revision: 'Modificare profil de consilier',
  advisory_service_revision: 'Modificare serviciu de consultanță',
  event: 'Eveniment',
  // A change to an event already on the public calendar, decided on as a change (tor.md §6.11).
  event_revision: 'Modificare eveniment',
  funding_application: 'Cerere de finanțare',
  funding_measure: 'Măsură de finanțare',
  contact_request: 'Cerere de contact',
};

// Workflow action codes are free-form (WorkflowDesignerPage lets admins define custom
// ones), so this is a best-effort label map, not an exhaustive enum — unmapped codes just
// render as-is. It stays the Romanian base: the queue reads it through i18n/actionLabels.ts,
// which adds the English side without changing the shape any RO-only caller depends on.
export const ACTION_LABELS: Record<string, string> = {
  approve: 'Aprobă',
  reject: 'Respinge',
  // `publish` exists on one flow only, content-approval, where it is the decision that lets a
  // draft or a revision through — the same act `approve` names everywhere else. Labelling it
  // "Publică" described the side effect rather than the decision, and read as a separate,
  // optional step next to it.
  publish: 'Aprobă',
  submit: 'Transmite',
  return: 'Returnează',
  withdraw: 'Retrage',
  resubmit: 'Retrimite',
  // The five the queue could reach but had no word for, so the button announced its raw English
  // code to the approver. `retract` is not `withdraw`: withdraw is the author pulling their own
  // submission out of the queue, retract is a decision-maker taking published content off the
  // public site, so they cannot share a label.
  retract: 'Retrage din public',
  revert: 'Readu la ciornă',
  validate: 'Validează',
  conclude: 'Finalizează',
  radiate: 'Radiază',
};

export const ROLE_LABELS: Record<string, string> = {
  AUTHENTICATED: 'Utilizator autentificat',
  CONSILIER_AGRICOL: 'Consilier agricol',
  CAMERA_AGRICOLA: 'Camera agricolă',
  CCAR: 'CCAR',
  COMPANIE_INPUT: 'Companie de inputuri',
  AIPA: 'AIPA',
  MAIA_ADMIN: 'MAIA',
  CONTENT_ADMIN: 'Administrator de conținut',
  SYSTEM_ADMIN: 'Administrator de sistem',
  ORGANIZATIE_CERCETARE: 'Organizație de cercetare și inovare',
  INSTITUTIE_FORMARE: 'Instituție de formare profesională',
  FURNIZOR_CONSILIERE: 'Furnizor de servicii de consiliere',
  ASOCIATIE_PROFESIONALA: 'Asociație profesională',
  ANALYST: 'Analist / vizualizator rapoarte',
};

/**
 * The single role to show next to a user's name (profile-card subtitle) when multi-role
 * would otherwise render as a cluttered "A · B · C" list. Multi-role itself is real and
 * unaffected everywhere else — nav gating, permissions, and the full role/permission chip
 * list on /profil all still use the complete `roles` array. This only picks what to show
 * in the one-line identity subtitle: the first role that isn't the automatic base grant
 * every account gets on first login, falling back to it if that's genuinely all there is.
 */
export function primaryRole(roles: string[]): string | undefined {
  return roles.find((r) => r !== 'AUTHENTICATED') ?? roles[0];
}

/**
 * What a state code MEANS, as one of the five tones — the classification the tag colours by.
 *
 * <p>Two questions are answered from here, and that is the point: the colour a reader sees and the
 * group a row is filed under are the same answer, so a green "Aprobată" cannot turn up under "În
 * examinare" again. It did, because that list used its own rule — everything not DRAFT and not
 * REJECTED — and ten published events whose instances stay open passed it.
 *
 * <p>The tone reaches the screen through statusTag.ts, which turns it into MUD's `.status-tag`
 * variant. Nothing renders a class name from here: this function returns what a state means, not
 * what it looks like, which is why the approval queue can read it without reading a stylesheet.
 * `info` is the fallback, and it is a real answer rather than a failure — a contact request that
 * has just arrived is NEW, nothing has been decided about it, and blue says exactly that.
 */
export function stateTone(state: string): BadgeTone {
  const s = state.toUpperCase();
  // Withdrawn is neutral, checked before the danger bucket it used to sit in. Everything left in
  // that bucket — rejected, radiated, suspended, blocked — is a decision taken about someone;
  // withdrawing is the one thing on the list a person does to their own dossier, and it can be
  // undone. Red made a participant's own choice look like a verdict against them, and made the
  // same word red on the training screen and grey on "Cererile mele", which BADGE_VARIANTS had
  // already settled for exactly this reason.
  // DELETED is neutral for the same reason and one more: the subject of the dossier is gone, so it
  // was neither approved nor refused, and the approval queue — which files rows by this tone — must
  // not read it as `info` and show it among the work still to be decided.
  // DEACTIVATED joins them: an account switched off is not informative, which is what the `info`
  // blue it used to fall through to said on the accounts screen. Nothing groups by this code — it
  // is an account status, never a workflow instance's state — so the approval queue, which files a
  // neutral row under no group at all, cannot see it.
  if (/WITHDRAWN|RETRAS|DELETED|DEACTIVATED/.test(s)) return 'neutral';
  // Danger checked before ok: INACTIVE contains ACTIVE as a substring, so checking
  // ok first mis-tagged an inactive state as a positive/final outcome (green).
  if (/(REJECT|RADIAT|SUSPEND|BLOCKED|INACTIVE)/.test(s)) return 'danger';
  // CONCLUDED and ARCHIVED are the event lifecycle's own terminal states (PUBLIC_EVENT_STATE in
  // cabinet/dossier/dossierStatus.ts, which has treated them as published since it was written).
  // Without them here a concluded event read as `info`: a blue badge, and — because the approval
  // queue groups by this tone — a decided dossier sitting among the work still to be done.
  if (
    /(APPROVED|PUBLISHED|VALIDATED|ATTESTED|ACTIVE|COMPLETED|CONCLUDED|ARCHIVED|CERTIFIED|CONFIRMED|FINAL|RESOLVED)/.test(
      s,
    )
  )
    return 'ok';
  if (/(PENDING|SUBMITTED|REVIEW|VALIDATION|IN_)/.test(s)) return 'warn';
  return 'info';
}

export function initials(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
